// HTTP client (axios) — handles:
// - Bearer access-token header (in-memory)
// - Automatic refresh on 401 + queued retry
// - Standard envelope unwrap (returns data / throws on .success === false)
//
// Refresh tokens live in an httpOnly cookie at /api/v1/auth (set by BE);
// the browser sends it automatically. We just call POST /auth/refresh and
// store the new access token.

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import type { Envelope, ErrorDetail } from "./types";
import { getViewAsCustomer, shouldInjectAsCustomer } from "./view-as";

export const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "/api/v1";

let accessToken: string | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null) { accessToken = token; }
export function getAccessToken(): string | null { return accessToken; }
export function onAuthLostCallback(cb: () => void) { onAuthLost = cb; }

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,        // send the refresh cookie on /auth/refresh
  headers: { "Content-Type": "application/json" },
});

axiosInstance.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  if (accessToken && cfg.headers) {
    cfg.headers.Authorization = `Bearer ${accessToken}`;
  }
  // View-as: inject ?as_customer=<id> for accountant on customer endpoints
  // per doc/02 §4.2.1. The BE validates accountant role + audit-logs it.
  const target = getViewAsCustomer();
  if (target && cfg.url && shouldInjectAsCustomer(cfg.url)) {
    cfg.params = { ...(cfg.params || {}), as_customer: target.user_id };
  }
  return cfg;
});

let refreshing: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await axios.post<Envelope<{ access_token: string }>>(
          `${API_BASE}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        if (res.data?.success && res.data.data?.access_token) {
          accessToken = res.data.data.access_token;
          return accessToken;
        }
      } catch {
        // fall through
      }
      accessToken = null;
      onAuthLost?.();
      return null;
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
}

axiosInstance.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (
      err.response?.status === 401 &&
      original &&
      !original._retry &&
      // Don't try to refresh the refresh / login endpoints themselves.
      !original.url?.endsWith("/auth/refresh") &&
      !original.url?.endsWith("/auth/login")
    ) {
      original._retry = true;
      const newToken = await tryRefresh();
      if (newToken) {
        if (!original.headers) original.headers = {} as any;
        (original.headers as any).Authorization = `Bearer ${newToken}`;
        return axiosInstance(original);
      }
    }
    return Promise.reject(err);
  },
);

// ---- Standard helpers — unwrap envelope and throw on !success ---------------

export class ApiError extends Error {
  code: string;
  status: number;
  errors: ErrorDetail[];
  data?: unknown;
  constructor(opts: { message: string; code: string; status: number; errors?: ErrorDetail[]; data?: unknown }) {
    super(opts.message);
    this.code = opts.code;
    this.status = opts.status;
    this.errors = opts.errors ?? [];
    this.data = opts.data;
  }
}

function unwrap<T>(res: AxiosResponse<Envelope<T>>): T {
  const env = res.data;
  if (!env || env.success === false) {
    throw new ApiError({
      code: env?.code || "UNKNOWN",
      message: env?.message || "เกิดข้อผิดพลาด",
      status: res.status,
      errors: env?.errors || [],
      data: env?.data,
    });
  }
  return env.data as T;
}

function asApiError(err: unknown): never {
  if (err instanceof ApiError) throw err;
  if (axios.isAxiosError(err)) {
    const env = err.response?.data as Envelope<unknown> | undefined;
    throw new ApiError({
      code: env?.code || "NETWORK_ERROR",
      message: env?.message || err.message,
      status: err.response?.status ?? 0,
      errors: env?.errors || [],
      data: env?.data,
    });
  }
  throw new ApiError({ code: "UNKNOWN", message: String(err), status: 0 });
}

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const res = await axiosInstance.get<Envelope<T>>(url, config);
    return unwrap(res);
  } catch (e) { asApiError(e); }
}

export async function apiPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  try {
    const res = await axiosInstance.post<Envelope<T>>(url, body, config);
    return unwrap(res);
  } catch (e) { asApiError(e); }
}

export async function apiPatch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  try {
    const res = await axiosInstance.patch<Envelope<T>>(url, body, config);
    return unwrap(res);
  } catch (e) { asApiError(e); }
}

export async function apiPut<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  try {
    const res = await axiosInstance.put<Envelope<T>>(url, body, config);
    return unwrap(res);
  } catch (e) { asApiError(e); }
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const res = await axiosInstance.delete<Envelope<T>>(url, config);
    return unwrap(res);
  } catch (e) { asApiError(e); }
}

// SWR fetcher — gives the unwrapped data with full envelope-aware error.
export const swrFetcher = <T>(url: string) => apiGet<T>(url);

// Form-data POST (used for file uploads). Accepts axios config so callers
// can pass ``onUploadProgress`` (the only practical way to surface bytes-
// transmitted progress in the UI; without this the modal sits silent for
// the multi-GB upload window).
export async function apiPostForm<T>(
  url: string,
  form: FormData,
  config?: AxiosRequestConfig,
): Promise<T> {
  try {
    const res = await axiosInstance.post<Envelope<T>>(url, form, {
      ...config,
      headers: { "Content-Type": "multipart/form-data", ...(config?.headers || {}) },
    });
    return unwrap(res);
  } catch (e) { asApiError(e); }
}

export { axiosInstance };
